import { useEffect, useState } from "react";
import { APICategory } from "../../../../../../models/Entities";
import { isEnabledCommonModel, SortedModelsMap } from "../../CommonModelUtils";
import { loadCommonModelsForCategory } from "./useCommonModelsForCategory";

// Count of enabled Common Models in a category: top-level (endpoint-backed) objects, excluding
// custom objects, that have at least one enabled model action. Matches what ScopesTable renders as
// a Common Model row. Fields don't count — only whole models.
const countEnabledCommonModels = (models: SortedModelsMap): number =>
  Object.values(models).filter(isEnabledCommonModel).length;

/**
 * Picks the category to land on when the user opens the scopes page without a specific category in
 * the URL: the one where the org has enabled the most Common Models. Ties (including an org with no
 * scopes enabled anywhere) resolve to the earliest category in the passed list. Returns null while
 * the per-category counts are still loading so the caller can hold off redirecting.
 */
export const useDefaultScopesCategory = (categories: APICategory[]): APICategory | null => {
  const [category, setCategory] = useState<APICategory | null>(null);
  // Stable dependency so the effect doesn't re-run on every render (categories is rebuilt each time).
  const categoriesKey = categories.join(",");

  // Choosing the landing category needs every category's enabled-model count, so fan out one fetch
  // per category and land on the highest count. Conscious trade-off: this is N parallel requests,
  // but only on a cold no-category landing. loadCommonModelsForCategory reuses the module cache, so
  // a warm cache fires zero requests and the chosen category's page then mounts without refetching.
  useEffect(() => {
    if (!categories.length) {
      setCategory(null);
      return undefined;
    }

    let active = true;
    Promise.all(
      categories.map((apiCategory) =>
        loadCommonModelsForCategory(apiCategory).then((models) => ({
          apiCategory,
          count: models ? countEnabledCommonModels(models) : 0,
        })),
      ),
    )
      .then((results) => {
        if (!active) return;
        // reduce keeps the earlier category on ties (strict >), so list order is the tiebreak.
        const best = results.reduce((winner, current) =>
          current.count > winner.count ? current : winner,
        );
        setCategory(best.apiCategory);
      })
      .catch(() => {
        if (active) setCategory(categories[0]);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesKey]);

  return category;
};
